import { supabase } from '../lib/supabaseClient';
import { backgroundSyncService } from './backgroundSyncService';

interface HealthMetric {
  metric_name: string;
  value: number;
  status: 'healthy' | 'warning' | 'critical';
  timestamp: string;
  details?: any;
}

class HealthDashboardService {
  private metrics: HealthMetric[] = [];
  private isMonitoring = false;

  startMonitoring() {
    if (this.isMonitoring) return;
    this.isMonitoring = true;

    // Her 30 saniyede bir health check
    setInterval(() => {
      this.collectMetrics();
    }, 30000);

    // Sayfa yüklendiğinde ilk check
    this.collectMetrics();
  }

  private async collectMetrics() {
    const metrics: HealthMetric[] = [];

    // API Response Time
    const apiStart = Date.now();
    try {
      await supabase.from('profiles').select('id').limit(1);
      const apiTime = Date.now() - apiStart;
      metrics.push({
        metric_name: 'api_response_time',
        value: apiTime,
        status: apiTime < 1000 ? 'healthy' : apiTime < 3000 ? 'warning' : 'critical',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      metrics.push({
        metric_name: 'api_response_time',
        value: -1,
        status: 'critical',
        timestamp: new Date().toISOString(),
        details: { error: error.message }
      });
    }

    // Memory Usage
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      const memoryUsage = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
      metrics.push({
        metric_name: 'memory_usage',
        value: memoryUsage,
        status: memoryUsage < 70 ? 'healthy' : memoryUsage < 90 ? 'warning' : 'critical',
        timestamp: new Date().toISOString()
      });
    }

    // Background Sync Queue
    const syncStats = backgroundSyncService.getTaskStats();
    const queueHealth = syncStats.failed > 5 ? 'critical' : syncStats.pending > 10 ? 'warning' : 'healthy';
    metrics.push({
      metric_name: 'sync_queue_health',
      value: syncStats.pending,
      status: queueHealth,
      timestamp: new Date().toISOString(),
      details: syncStats
    });

    // Page Load Performance
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigation) {
      const loadTime = navigation.loadEventEnd - navigation.fetchStart;
      metrics.push({
        metric_name: 'page_load_time',
        value: loadTime,
        status: loadTime < 2000 ? 'healthy' : loadTime < 5000 ? 'warning' : 'critical',
        timestamp: new Date().toISOString()
      });
    }

    this.metrics = metrics;

    // Critical durumları hemen rapor et
    const criticalMetrics = metrics.filter(m => m.status === 'critical');
    if (criticalMetrics.length > 0) {
      backgroundSyncService.addTask('analytics', {
        table: 'health_alerts',
        data: { metrics: criticalMetrics, alert_level: 'critical' }
      }, 'high');
    }

    // Tüm metrikleri background'da gönder
    backgroundSyncService.addTask('analytics', {
      table: 'health_metrics',
      data: { metrics, collected_at: new Date().toISOString() }
    }, 'low');
  }

  getCurrentHealth() {
    const criticalCount = this.metrics.filter(m => m.status === 'critical').length;
    const warningCount = this.metrics.filter(m => m.status === 'warning').length;

    return {
      overall_status: criticalCount > 0 ? 'critical' : warningCount > 0 ? 'warning' : 'healthy',
      metrics: this.metrics,
      summary: {
        total: this.metrics.length,
        healthy: this.metrics.filter(m => m.status === 'healthy').length,
        warning: warningCount,
        critical: criticalCount
      }
    };
  }

  stopMonitoring() {
    this.isMonitoring = false;
  }
}

export const healthDashboardService = new HealthDashboardService();
